class BbdBackInStockDialog extends HTMLElement {
  #dialog = null;
  #form = null;
  #previousScrollY = 0;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#form = this.querySelector('.bbd-back-in-stock__form');
    this.#form?.addEventListener('submit', this.#handleSubmit.bind(this));
    this.querySelector('.bbd-back-in-stock__close')?.addEventListener('click', () => this.#close());
    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#close();
    });
    this.#dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close();
    });
  }

  open({ variantId, productId } = {}) {
    document.querySelector('quick-add-dialog')?.closeDialog?.();

    const variantInput = this.querySelector('[ref="variantId"]');
    const productInput = this.querySelector('[ref="productId"]');
    if (variantInput) variantInput.value = variantId || '';
    if (productInput) productInput.value = productId || '';

    this.#resetForm();
    this.#openDialog();
  }

  #openDialog() {
    if (!this.#dialog || this.#dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.documentElement.setAttribute('scroll-lock', '');
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      this.#dialog.showModal();
    });
  }

  async #close() {
    if (!this.#dialog?.open) return;

    this.#dialog.classList.add('dialog-closing');

    await new Promise((resolve) => {
      const onEnd = () => resolve();
      this.#dialog.addEventListener('animationend', onEnd, { once: true });
      setTimeout(resolve, 300);
    });

    const otherOpenDialogs = Array.from(document.querySelectorAll('dialog[open]')).filter((d) => d !== this.#dialog);
    const activeLockers = Array.from(document.querySelectorAll('[scroll-lock]:not(html)')).filter((el) => {
      if (el.tagName === 'DIALOG') return false;
      if (el.tagName === 'DETAILS') return el.open;
      return el.offsetWidth > 0 && el.offsetHeight > 0;
    });

    if (otherOpenDialogs.length === 0 && activeLockers.length === 0) {
      document.documentElement.removeAttribute('scroll-lock');
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    this.#dialog.close();
    this.#dialog.classList.remove('dialog-closing');
  }

  #resetForm() {
    const formWrapper = this.querySelector('.bbd-back-in-stock__form-wrapper');
    const success = this.querySelector('.bbd-back-in-stock__success');
    const error = this.querySelector('.bbd-back-in-stock__error');
    const emailInput = this.querySelector('input[type="email"]');
    const marketingCheckbox = this.querySelector('input[name="marketing"]');

    formWrapper?.removeAttribute('hidden');
    success?.setAttribute('hidden', '');
    error?.setAttribute('hidden', '');
    this.classList.remove('bbd-back-in-stock--loading');
    if (emailInput) emailInput.value = '';
    if (marketingCheckbox) marketingCheckbox.checked = false;
  }

  async #handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const emailInput = form.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim();

    if (!email) {
      emailInput?.focus();
      return;
    }

    const variantId = this.querySelector('[ref="variantId"]')?.value;
    const marketingCheckbox = form.querySelector('input[name="marketing"]');
    const marketingConsented = marketingCheckbox?.checked === true;

    const apiKey = this.dataset.apiKey || '';
    const listId = this.dataset.listId || '';
    const profileProperties = {
      language: this.dataset.locale || '',
      country: this.dataset.country || '',
      market: this.dataset.market || '',
      currency: this.dataset.currency || ''
    };

    this.#setLoading(true);

    const calls = [
      this.#subscribeToBackInStock(email, variantId, apiKey),
      this.#upsertProfile(email, profileProperties, apiKey)
    ];

    if (marketingConsented && listId) {
      calls.push(this.#subscribeToMarketingList(email, profileProperties, apiKey, listId));
    }

    Promise.allSettled(calls).then((results) => {
      this.#setLoading(false);
      const bisResult = results[0];
      if (bisResult.status === 'fulfilled' && bisResult.value.ok) {
        this.#setSuccess();
      } else {
        this.#setError();
      }
    });
  }

  async #subscribeToBackInStock(email, variantId, apiKey) {
    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(apiKey)}`;
    return fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } }
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } }
          }
        }
      })
    });
  }

  async #upsertProfile(email, profileProperties, apiKey) {
    const url = `https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(apiKey)}`;
    return fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: { email, properties: profileProperties }
        }
      })
    });
  }

  async #subscribeToMarketingList(email, profileProperties, apiKey, listId) {
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(apiKey)}`;
    return fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back In Stock Form',
            profile: { data: { type: 'profile', attributes: { email, properties: profileProperties } } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      })
    });
  }

  #setLoading(loading) {
    const submit = this.querySelector('.bbd-back-in-stock__submit');
    if (loading) {
      this.classList.add('bbd-back-in-stock--loading');
      submit?.setAttribute('disabled', '');
    } else {
      this.classList.remove('bbd-back-in-stock--loading');
      submit?.removeAttribute('disabled');
    }
  }

  #setSuccess() {
    const formWrapper = this.querySelector('.bbd-back-in-stock__form-wrapper');
    const success = this.querySelector('.bbd-back-in-stock__success');
    formWrapper?.setAttribute('hidden', '');
    success?.removeAttribute('hidden');
  }

  #setError() {
    const error = this.querySelector('.bbd-back-in-stock__error');
    error?.removeAttribute('hidden');
  }
}

if (!customElements.get('bbd-back-in-stock-dialog')) {
  customElements.define('bbd-back-in-stock-dialog', BbdBackInStockDialog);
}

document.addEventListener('variant:update', (event) => {
  const resource = event.detail?.resource;
  if (!resource) return;

  const context = event.target.closest('.shopify-section, .quick-add-modal__content, quick-add-component');
  if (!context) return;

  const available = resource.available === true;
  const variantId = String(resource.id);

  context.querySelectorAll('.bbd-back-in-stock__trigger').forEach((trigger) => {
    trigger.dataset.bisVariantId = variantId;
    trigger.hidden = available;
  });

  context.querySelectorAll('product-form-component').forEach((form) => {
    form.hidden = !available;
  });

  context.querySelectorAll('.sticky-add-to-cart__button').forEach((btn) => {
    btn.hidden = !available;
  });
});

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('.bbd-back-in-stock__trigger');
  if (!trigger) return;

  document.querySelector('bbd-back-in-stock-dialog')?.open({
    variantId: trigger.dataset.bisVariantId,
    productId: trigger.dataset.bisProductId
  });
});
